import React, { useEffect, useState } from 'react';
import { Link, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  getFrequencyDashboardStats, getFrequencyEditions, getFrequencyEdition, createFrequencyEdition,
  updateFrequencyEdition, deleteFrequencyEdition, previewFrequencyEdition, publishFrequencyEdition,
  nudgeFrequencyEdition, getFrequencyDepartments, createFrequencyDepartment, updateFrequencyDepartment,
  deleteFrequencyDepartment, getFrequencySubmissions, createFrequencySubmission, updateFrequencySubmission,
  approveFrequencySubmission, returnFrequencySubmission, getFrequencyQueries, assignFrequencyQuery,
  respondFrequencyQuery, updateFrequencyQueryStatus, getFrequencyEditionAnalytics,
  getFrequencySettings, updateFrequencySettings, getFrequencyAccess, getFrequencySubmission,
  downloadFrequencyRecipientsTemplate, uploadFrequencyRecipients, clearFrequencyRecipients,
  parseFrequencyRecipients, keepSubmissionForNewsletter, publishSubmissionImmediately,
  getFrequencyImmediateAnalytics, sendFrequencyWhatsApp, sendFrequencyTestReminder,
} from '../lib/api';
import { CommDirectoryPage, UpdatesListPage, UpdateEditorPage, UpdateAnalyticsPage } from './FrequencyCommsPages';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Radio, FileText, Send, Eye, Pencil, Trash2, Plus, CheckCircle2, RotateCcw, Megaphone,
  Inbox, Settings as SettingsIcon, BarChart3, Layers, Users2, Bell, AlertCircle, ChevronLeft, Clock,
  Upload, Download, Zap, Users, X, MessageCircle,
} from 'lucide-react';

const STATUS_COLOR = {
  Draft: 'bg-gray-200 text-gray-800',
  Submitted: 'bg-blue-100 text-blue-700',
  'In Review': 'bg-amber-100 text-amber-700',
  Approved: 'bg-emerald-100 text-emerald-700',
  Returned: 'bg-rose-100 text-rose-700',
  Published: 'bg-emerald-100 text-emerald-700',
  New: 'bg-blue-100 text-blue-700',
  Assigned: 'bg-amber-100 text-amber-700',
  'In Progress': 'bg-indigo-100 text-indigo-700',
  'Awaiting Response': 'bg-yellow-100 text-yellow-700',
  Resolved: 'bg-emerald-100 text-emerald-700',
  Closed: 'bg-gray-200 text-gray-700',
};

// ============== SHELL ==============
export function FrequencyShell() {
  const { user } = useAuth();
  const location = useLocation();
  const [access, setAccess] = useState(null);
  useEffect(() => { (async () => {
    try { const r = await getFrequencyAccess(); setAccess(r.data); }
    catch { setAccess({ has_access: false, can_manage: false, is_department_owner: false }); }
  })(); }, []);
  if (!user) return null;
  if (!access) return <div className="text-center py-12 text-muted-foreground">Loading…</div>;
  const canManage = access.can_manage;
  const isSuper = access.is_super;
  const canCreate = access.can_create_update;
  if (!access.has_access) {
    return <Card><CardContent className="p-8 text-center text-muted-foreground" data-testid="freq-no-access">The Frequency is restricted to Marketing Admin, Super Admin, and registered Department Owners. Ask your admin for access.</CardContent></Card>;
  }
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Radio, to: '/frequency', match: '/frequency' },
    { id: 'updates', label: 'Updates', icon: Megaphone, to: '/frequency/updates', match: '/frequency/updates' },
    { id: 'directory', label: 'Directory', icon: Users2, to: '/frequency/directory', match: '/frequency/directory', superOnly: true },
    { id: 'queries', label: 'Query Center', icon: Inbox, to: '/frequency/queries', match: '/frequency/queries' },
    { id: 'settings', label: 'Settings', icon: SettingsIcon, to: '/frequency/settings', match: '/frequency/settings', mgrOnly: true },
  ].filter(t => (!t.mgrOnly || canManage) && (!t.superOnly || isSuper));
  return (
    <div className="space-y-6" data-testid="frequency-shell">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#FF6B6B] via-[#FFD93D] to-[#6BCB77] flex items-center justify-center">
            <Megaphone className="h-5 w-5 text-white drop-shadow" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold tracking-tight">The Frequency</h1>
            <p className="text-xs text-muted-foreground">Internal newsletter & communication module</p>
          </div>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 border-b border-border" data-testid="frequency-tabs">
        {tabs.map(t => {
          const Icon = t.icon;
          const active = location.pathname === t.match || (t.match !== '/frequency' && location.pathname.startsWith(t.match));
          return (
            <Link key={t.id} to={t.to}
              className={`inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-all ${active ? 'bg-gradient-to-r from-[#FF6B6B]/15 to-[#FFD93D]/15 text-foreground border border-[#FF6B6B]/30' : 'text-muted-foreground hover:bg-accent'}`}
              data-testid={`freq-tab-${t.id}`}>
              <Icon className="h-4 w-4" />{t.label}
            </Link>
          );
        })}
      </div>
      <Routes>
        <Route index element={<FrequencyDashboardPage canManage={canManage} />} />
        <Route path="updates" element={canCreate ? <UpdatesListPage /> : <Navigate to="/frequency" />} />
        <Route path="updates/new" element={canCreate ? <UpdateEditorPage /> : <Navigate to="/frequency" />} />
        <Route path="updates/:id/analytics" element={canCreate ? <UpdateAnalyticsPage /> : <Navigate to="/frequency" />} />
        <Route path="updates/:id" element={canCreate ? <UpdateEditorPage /> : <Navigate to="/frequency" />} />
        <Route path="directory" element={isSuper ? <CommDirectoryPage /> : <Navigate to="/frequency" />} />
        <Route path="queries" element={<FrequencyQueriesPage canManage={canManage} />} />
        <Route path="settings" element={canManage ? <FrequencySettingsPage /> : <Navigate to="/frequency" />} />
      </Routes>
    </div>
  );
}

// ============== DASHBOARD ==============
function FrequencyDashboardPage({ canManage }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { (async () => {
    try { const r = await getFrequencyDashboardStats(); setStats(r.data); }
    catch { setStats({}); }
  })(); }, []);
  if (!stats) return <div className="text-center py-12 text-muted-foreground">Loading…</div>;
  // Department Owner scoped view
  if (stats.scope === 'department_owner') {
    return (
      <div className="space-y-6">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Megaphone className="h-5 w-5 text-[#F5A623]" />
          <div>
            <p className="font-medium">Showing analytics for <span className="text-[#F5A623]">{stats.department}</span></p>
            <p className="text-xs text-muted-foreground">Your department metrics in The Frequency</p>
          </div>
        </CardContent></Card>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard label="Total Updates Submitted" value={stats.total_submitted || 0} accent="from-[#4D9BF5] to-[#7B7DFF]" testid="freq-stat-total-submitted" />
          <StatCard label="On-Time Submissions" value={stats.on_time_submissions || 0} accent="from-[#6BCB77] to-[#3DDC97]" testid="freq-stat-ontime" />
          <StatCard label="Published Editions" value={stats.published_editions || 0} accent="from-[#7B7DFF] to-[#9D6BFF]" testid="freq-stat-pub" />
          <StatCard label="Open Queries" value={stats.open_queries || 0} accent="from-[#FF6B6B] to-[#FF8FB1]" testid="freq-stat-open-q" />
          <StatCard label="Resolved Queries" value={stats.resolved_queries || 0} accent="from-[#6BCB77] to-[#3DDC97]" testid="freq-stat-resolved-q" />
          <StatCard label="Approved Submissions" value={stats.approved || 0} accent="from-[#FFD93D] to-[#FFA94D]" testid="freq-stat-approved-owner" />
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Departments" value={stats.total_departments || 0} accent="from-[#6BCB77] to-[#3DDC97]" testid="freq-stat-depts" />
        <StatCard label="Submitted" value={stats.submitted || 0} accent="from-[#4D9BF5] to-[#7B7DFF]" testid="freq-stat-submitted" />
        <StatCard label="Pending" value={stats.pending || 0} accent="from-[#FFA94D] to-[#FF6B6B]" testid="freq-stat-pending" />
        <StatCard label="Completion" value={`${stats.completion_pct || 0}%`} accent="from-[#FFD93D] to-[#FFA94D]" testid="freq-stat-completion" />
        <StatCard label="Approved" value={stats.approved || 0} accent="from-[#6BCB77] to-[#3DDC97]" testid="freq-stat-approved" />
        <StatCard label="Published Editions" value={stats.published_editions || 0} accent="from-[#7B7DFF] to-[#9D6BFF]" testid="freq-stat-published" />
        <StatCard label="Open Queries" value={stats.queries_open || 0} accent="from-[#FF6B6B] to-[#FF8FB1]" testid="freq-stat-queries-open" />
        <StatCard label="Pending Acknowledgement" value={stats.pending_ack || 0} accent="from-[#FFA94D] to-[#FFD93D]" testid="freq-stat-pending-ack" />
      </div>
      {stats.latest_edition && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Latest Edition · <Badge>{stats.latest_edition.status}</Badge></CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex gap-3 flex-wrap">
              <div><span className="text-muted-foreground">Title:</span> {stats.latest_edition.title}</div>
              <div><span className="text-muted-foreground">Period:</span> {stats.latest_edition.period}</div>
              <div><span className="text-muted-foreground">Publish date:</span> {(stats.latest_edition.publish_at || stats.latest_edition.submission_deadline || '—').slice(0, 16).replace('T', ' ')}</div>
            </div>
            {canManage && (
              <div className="pt-2">
                <Button asChild size="sm" variant="outline">
                  <Link to={`/frequency/updates`}>Open Updates</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <div className={`mt-2 inline-block bg-gradient-to-r ${accent} bg-clip-text text-transparent text-3xl font-bold`}>{value}</div>
      </CardContent>
    </Card>
  );
}

// ============== SETTINGS ==============
function FrequencySettingsPage() {
  const [settings, setSettings] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [deptForm, setDeptForm] = useState({ name: '', owners: [{ email: '', name: '' }], active: true });
  const [editingDept, setEditingDept] = useState(null);
  const [deptDialog, setDeptDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const sendTestReminder = async () => {
    setSendingTest(true);
    try { const r = await sendFrequencyTestReminder(); toast.success(`Test reminder sent to ${r.data.sent_to}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not send test reminder'); }
    finally { setSendingTest(false); }
  };

  const load = async () => {
    const [s, d] = await Promise.all([getFrequencySettings(), getFrequencyDepartments()]);
    setSettings(s.data); setDepartments(d.data);
  };
  useEffect(() => { load().catch(() => toast.error('Failed to load settings')); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await updateFrequencySettings({
        sender_email: settings.sender_email,
        sender_name: settings.sender_name,
        brand_color: settings.brand_color,
        email_template_html: settings.email_template_html,
      });
      toast.success('Settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const saveDept = async () => {
    const owners = (deptForm.owners || [])
      .map(o => ({ email: (o.email || '').trim(), name: (o.name || '').trim() }))
      .filter(o => o.email);
    if (!deptForm.name.trim()) { toast.error('Department name is required'); return; }
    if (owners.length === 0) { toast.error('At least one owner email is required'); return; }
    const payload = { name: deptForm.name, owners, active: deptForm.active };
    try {
      if (editingDept) {
        await updateFrequencyDepartment(editingDept.id, payload);
        toast.success('Department updated');
      } else {
        await createFrequencyDepartment(payload);
        toast.success('Department added');
      }
      setDeptDialog(false); setEditingDept(null);
      setDeptForm({ name: '', owners: [{ email: '', name: '' }], active: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const setOwner = (idx, field, value) => {
    setDeptForm(prev => {
      const owners = [...(prev.owners || [])];
      owners[idx] = { ...owners[idx], [field]: value };
      return { ...prev, owners };
    });
  };
  const addOwnerRow = () => setDeptForm(prev => ({ ...prev, owners: [...(prev.owners || []), { email: '', name: '' }] }));
  const removeOwnerRow = (idx) => setDeptForm(prev => {
    const owners = (prev.owners || []).filter((_, i) => i !== idx);
    return { ...prev, owners: owners.length ? owners : [{ email: '', name: '' }] };
  });

  const removeDept = async (d) => {
    if (!window.confirm(`Delete ${d.name}?`)) return;
    try { await deleteFrequencyDepartment(d.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (!settings) return <div className="text-center py-12 text-muted-foreground">Loading…</div>;

  return (
    <Tabs defaultValue="general" className="space-y-4">
      <TabsList>
        <TabsTrigger value="general" data-testid="settings-tab-general">General</TabsTrigger>
        <TabsTrigger value="departments" data-testid="settings-tab-depts">Departments</TabsTrigger>
        <TabsTrigger value="template" data-testid="settings-tab-template">Email Template</TabsTrigger>
      </TabsList>

      <TabsContent value="general" className="space-y-4">
        <Card><CardContent className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Sender Email</Label>
              <Input value={settings.sender_email || ''} onChange={e => setSettings({ ...settings, sender_email: e.target.value })} data-testid="sender-email-input" />
            </div>
            <div><Label>Sender Name</Label>
              <Input value={settings.sender_name || ''} onChange={e => setSettings({ ...settings, sender_name: e.target.value })} data-testid="sender-name-input" />
            </div>
            <div><Label>Brand Color</Label>
              <Input type="color" value={settings.brand_color || '#0F172A'} onChange={e => setSettings({ ...settings, brand_color: e.target.value })} className="h-10 w-20 p-1" data-testid="brand-color-input" />
            </div>
          </div>
          <Button onClick={saveSettings} disabled={saving} className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" data-testid="save-settings-btn">Save</Button>
        </CardContent></Card>
        <Card><CardContent className="p-6 space-y-3">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><Bell className="h-4 w-4 text-[#2563EB]" /> Monthly Update Reminder</h3>
            <p className="text-sm text-muted-foreground">On the <strong>1st of every month</strong>, every Frequency department owner automatically receives an email asking them to share their team update via the app (with a login button to the Updates tab). No configuration needed.</p>
          </div>
          <Button variant="outline" onClick={sendTestReminder} disabled={sendingTest} data-testid="send-test-reminder-btn">
            <Send className="h-4 w-4 mr-1" />{sendingTest ? 'Sending…' : 'Send me a test reminder'}
          </Button>
        </CardContent></Card>
      </TabsContent>

      <TabsContent value="departments">
        <Card><CardContent className="p-6 space-y-4">
          <div className="flex justify-between items-center">
            <div><h3 className="font-semibold">Frequency Departments</h3>
              <p className="text-sm text-muted-foreground">Independent of UniWork&apos;s task departments. Each owner can submit on their department&apos;s behalf.</p>
            </div>
            <Button onClick={() => { setEditingDept(null); setDeptForm({ name: '', owners: [{ email: '', name: '' }], active: true }); setDeptDialog(true); }} className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" data-testid="add-freq-dept-btn"><Plus className="h-4 w-4 mr-1" /> Add Department</Button>
          </div>
          <div className="space-y-2">
            {departments.length === 0 && <p className="text-sm text-muted-foreground">No departments yet.</p>}
            {departments.map(d => (
              <div key={d.id} className="flex items-center justify-between p-3 rounded-lg border border-border" data-testid={`freq-dept-row-${d.id}`}>
                <div className="flex-1">
                  <p className="font-medium">{d.name} {!d.active && <Badge className="ml-2 bg-gray-200 text-gray-700">Inactive</Badge>}</p>
                  <p className="text-xs text-muted-foreground">
                    {(d.owners && d.owners.length)
                      ? d.owners.map(o => o.name ? `${o.name} (${o.email})` : o.email).join(', ')
                      : `${d.owner_name || '—'} · ${d.owner_email || ''}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setEditingDept(d); setDeptForm({ name: d.name, owners: (d.owners && d.owners.length) ? d.owners.map(o => ({ email: o.email, name: o.name || '' })) : [{ email: d.owner_email || '', name: d.owner_name || '' }], active: d.active }); setDeptDialog(true); }} data-testid={`edit-freq-dept-${d.id}`}><Pencil className="h-3 w-3" /></Button>
                  <Button size="sm" variant="outline" className="text-rose-500" onClick={() => removeDept(d)} data-testid={`del-freq-dept-${d.id}`}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent></Card>
        <Dialog open={deptDialog} onOpenChange={setDeptDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingDept ? 'Edit Department' : 'Add Department'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Department Name *</Label><Input value={deptForm.name} onChange={e => setDeptForm({ ...deptForm, name: e.target.value })} data-testid="dept-name-input" /></div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Owners *</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addOwnerRow} data-testid="add-owner-row-btn"><Plus className="h-3 w-3 mr-1" /> Add owner</Button>
                </div>
                {(deptForm.owners || []).map((o, idx) => (
                  <div key={idx} className="flex gap-2 items-start" data-testid={`owner-row-${idx}`}>
                    <Input className="flex-1" placeholder="Owner name" value={o.name} onChange={e => setOwner(idx, 'name', e.target.value)} data-testid={`owner-name-${idx}`} />
                    <Input className="flex-1" type="email" placeholder="owner@email.com" value={o.email} onChange={e => setOwner(idx, 'email', e.target.value)} data-testid={`owner-email-${idx}`} />
                    {(deptForm.owners || []).length > 1 && (
                      <Button type="button" size="icon" variant="ghost" className="text-rose-500 shrink-0" onClick={() => removeOwnerRow(idx)} data-testid={`remove-owner-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">All owners can submit, view queries routed to this department, and receive monthly reminders.</p>
              </div>
              <div className="flex items-center gap-2"><Switch checked={deptForm.active} onCheckedChange={(v) => setDeptForm({ ...deptForm, active: v })} /><Label>Active</Label></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeptDialog(false)}>Cancel</Button>
              <Button onClick={saveDept} disabled={!deptForm.name || !(deptForm.owners || []).some(o => (o.email || '').trim())} className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" data-testid="save-dept-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TabsContent>

      <TabsContent value="template">
        <Card><CardContent className="p-6 space-y-3">
          <Label>Email Template HTML</Label>
          <p className="text-xs text-muted-foreground">Available variables: <code>&#123;&#123;edition_title&#125;&#125;</code>, <code>&#123;&#123;edition_period&#125;&#125;</code>, <code>&#123;&#123;sections&#125;&#125;</code>, <code>&#123;&#123;ack_link&#125;&#125;</code>, <code>&#123;&#123;query_link&#125;&#125;</code></p>
          <Textarea rows={18} value={settings.email_template_html || ''} onChange={e => setSettings({ ...settings, email_template_html: e.target.value })} className="font-mono text-xs" data-testid="email-template-textarea" />
          <Button onClick={saveSettings} className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" disabled={saving}>Save Template</Button>
        </CardContent></Card>
      </TabsContent>
    </Tabs>
  );
}

// ============== EDITIONS LIST ==============
function FrequencyEditionsListPage() {
  const [editions, setEditions] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { (async () => {
    try { const r = await getFrequencyEditions(); setEditions(r.data); } catch (_) { /* noop */ }
  })(); }, []);
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Newsletter Editions</h2>
        <Button onClick={() => navigate('/frequency/editions/new')} className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" data-testid="new-edition-btn"><Plus className="h-4 w-4 mr-1" /> New Newsletter</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {editions.map(e => (
          <Card key={e.id} className="hover:border-[#F5A623]/40 transition-colors cursor-pointer" onClick={() => navigate(`/frequency/editions/${e.id}`)} data-testid={`edition-card-${e.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-lg">{e.title}</p>
                  <p className="text-sm text-muted-foreground">{e.period} · {e.edition_type}</p>
                </div>
                <Badge className={STATUS_COLOR[e.status] || ''}>{e.status}</Badge>
              </div>
              <div className="mt-3 text-xs text-muted-foreground space-y-1">
                <p><Clock className="h-3 w-3 inline mr-1" />Publish date: <strong>{(e.publish_at || e.submission_deadline || '—').slice(0, 16).replace('T', ' ')}</strong></p>
                {e.published_at && <p>Published: {new Date(e.published_at).toLocaleString()} · {e.sent_count} recipients</p>}
              </div>
            </CardContent>
          </Card>
        ))}
        {editions.length === 0 && <p className="text-sm text-muted-foreground col-span-2 text-center py-8">No editions yet.</p>}
      </div>
    </div>
  );
}

// ============== EDITION EDITOR (Create + Builder + Publish) ==============
// Convert a stored UTC ISO datetime to a value the datetime-local input shows in the user's local time.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso).slice(0, 16);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

function FrequencyEditionEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [edition, setEdition] = useState({ title: '', edition_type: 'Monthly', period: '', publish_at: '', reminder_dates: [], selected_department_ids: [], submission_ids: [], section_order: [], hidden_section_ids: [], recipients: [] });
  const [departments, setDepartments] = useState([]);
  const [allApprovedSubs, setAllApprovedSubs] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recipientSummary, setRecipientSummary] = useState(null);
  const [uploadingRec, setUploadingRec] = useState(false);
  const [sendingWa, setSendingWa] = useState(false);

  const load = async () => {
    const [d, s] = await Promise.all([getFrequencyDepartments(true), getFrequencySubmissions()]);
    setDepartments(d.data);
    setAllApprovedSubs(s.data.filter(x => x.status === 'Approved'));
    if (!isNew) {
      const e = await getFrequencyEdition(id);
      setEdition({ section_order: [], hidden_section_ids: [], submission_ids: [], recipients: [], ...e.data });
      const recs = e.data.recipients || [];
      if (recs.length) {
        const byDept = {};
        recs.forEach(r => { byDept[r.department] = (byDept[r.department] || 0) + 1; });
        setRecipientSummary({ total: recs.length, by_department: byDept, invalid: [] });
      } else { setRecipientSummary(null); }
    }
  };
  useEffect(() => { load(); }, [id]);

  const toggleDept = (deptId) => {
    setEdition(prev => ({ ...prev, selected_department_ids: prev.selected_department_ids?.includes(deptId) ? prev.selected_department_ids.filter(x => x !== deptId) : [...(prev.selected_department_ids || []), deptId] }));
  };

  const toggleSubmission = (subId) => {
    setEdition(prev => {
      const has = prev.submission_ids?.includes(subId);
      const next_ids = has ? prev.submission_ids.filter(x => x !== subId) : [...(prev.submission_ids || []), subId];
      const next_order = has ? (prev.section_order || []).filter(x => x !== subId) : [...(prev.section_order || []).filter(x => next_ids.includes(x)), subId];
      return { ...prev, submission_ids: next_ids, section_order: next_order };
    });
  };

  const moveSection = (subId, dir) => {
    const order = [...(edition.section_order || edition.submission_ids || [])];
    const idx = order.indexOf(subId);
    const newIdx = idx + dir;
    if (idx < 0 || newIdx < 0 || newIdx >= order.length) return;
    [order[idx], order[newIdx]] = [order[newIdx], order[idx]];
    setEdition({ ...edition, section_order: order });
  };

  const toggleHidden = (subId) => {
    const hidden = edition.hidden_section_ids || [];
    setEdition({ ...edition, hidden_section_ids: hidden.includes(subId) ? hidden.filter(x => x !== subId) : [...hidden, subId] });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (!isNew) {
        await updateFrequencyEdition(id, edition);
        toast.success('Newsletter saved');
        load();
      } else {
        const r = await createFrequencyEdition(edition);
        toast.success('Newsletter created');
        navigate(`/frequency/editions/${r.data.id}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const doPreview = async () => {
    try {
      const r = await previewFrequencyEdition(id);
      setPreviewHtml((r.data.html || '').replaceAll('__ORIGIN__', window.location.origin));
      setShowPreview(true);
    }
    catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
  };

  const doPublish = async () => {
    const recCount = recipientSummary?.total || (edition.recipients || []).length;
    if (!recCount) { toast.error('Upload a recipient list (Excel) before publishing.'); return; }
    if (!window.confirm(`Publish this newsletter to ${recCount} recipient(s)? Emails will be sent now.`)) return;
    try { const r = await publishFrequencyEdition(id, { publish_without_missing: true }); toast.success(`Published — ${r.data.sent_count} recipients`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await downloadFrequencyRecipientsTemplate();
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'frequency_recipients_template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const onUploadRecipients = async (file) => {
    if (!file) return;
    setUploadingRec(true);
    try {
      const r = await uploadFrequencyRecipients(id, file);
      setRecipientSummary(r.data);
      setEdition(prev => ({ ...prev, recipients: r.data.recipients }));
      toast.success(`${r.data.total} recipients loaded`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingRec(false); }
  };

  const removeRecipients = async () => {
    try { await clearFrequencyRecipients(id); setRecipientSummary(null); setEdition(prev => ({ ...prev, recipients: [] })); toast.success('Recipients cleared'); }
    catch { toast.error('Failed'); }
  };

  const doSendWhatsApp = async () => {
    const recCount = (edition.recipients || []).filter(r => r.phone).length;
    if (!recCount) { toast.error('No recipients have a phone number. Upload a list with a Phone column.'); return; }
    if (!window.confirm(`Send this edition via WhatsApp to ${recCount} recipient(s) with a phone number?`)) return;
    setSendingWa(true);
    try {
      const r = await sendFrequencyWhatsApp(id);
      toast.success(`WhatsApp queued for ${r.data.queued} recipient(s). Refreshing status…`);
      setTimeout(() => load(), 5000);
    } catch (e) { toast.error(e.response?.data?.detail || 'WhatsApp send failed'); }
    finally { setSendingWa(false); }
  };

  const isPublished = edition.status === 'Published';
  const chosenSubs = (edition.section_order || edition.submission_ids || []).map(sid => allApprovedSubs.find(s => s.id === sid)).filter(Boolean);
  const unchosenSubs = allApprovedSubs.filter(s => !(edition.submission_ids || []).includes(s.id));

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate('/frequency/editions')} size="sm"><ChevronLeft className="h-4 w-4 mr-1" />Back to Newsletter Editions</Button>

      <Card><CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-xl font-semibold">{isNew ? 'Create Newsletter' : 'Edit Newsletter'} {edition.status && <Badge className={STATUS_COLOR[edition.status]}>{edition.status}</Badge>}</h2>
          <div className="flex gap-2">
            {!isNew && <Button variant="outline" onClick={doPreview} data-testid="preview-edition-btn"><Eye className="h-4 w-4 mr-1" />Preview</Button>}
            {!isNew && !isPublished && <Button onClick={doPublish} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="publish-edition-btn"><Send className="h-4 w-4 mr-1" />Publish</Button>}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Title *</Label><Input value={edition.title} onChange={e => setEdition({ ...edition, title: e.target.value })} placeholder="June 2026 Edition" data-testid="edition-title-input" disabled={isPublished} /></div>
          <div><Label>Period</Label><Input value={edition.period} onChange={e => setEdition({ ...edition, period: e.target.value })} placeholder="June 2026" data-testid="edition-period-input" disabled={isPublished} /></div>
          <div><Label>Type</Label>
            <Select value={edition.edition_type} onValueChange={(v) => setEdition({ ...edition, edition_type: v })} disabled={isPublished}>
              <SelectTrigger data-testid="edition-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Monthly">Monthly</SelectItem><SelectItem value="Quarterly">Quarterly</SelectItem><SelectItem value="Special">Special</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label>Publish Date &amp; Time *</Label>
            <Input type="datetime-local" value={toLocalInput(edition.publish_at)} onChange={e => setEdition({ ...edition, publish_at: e.target.value ? new Date(e.target.value).toISOString() : '' })} data-testid="edition-publish-at-input" disabled={isPublished} />
            <p className="text-xs text-muted-foreground mt-1">The newsletter will be sent automatically at this time (your local time) once recipients &amp; approved content are set. You can also publish manually anytime.</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving || !edition.title || !edition.publish_at} className="bg-[#2563EB] hover:bg-[#1E40AF] text-white font-semibold" data-testid="save-edition-btn">{isNew ? 'Create' : 'Save'}</Button>
      </CardContent></Card>

      {!isNew && (
        <>
          <Card><CardContent className="p-6 space-y-3">
            <h3 className="font-semibold flex items-center gap-2"><Layers className="h-4 w-4" />Sections (ordered)</h3>
            <p className="text-xs text-muted-foreground">Pick approved submissions and arrange the order. Hide any section that shouldn&apos;t appear in this newsletter.</p>
            {chosenSubs.length === 0 && <p className="text-sm text-muted-foreground">No sections selected yet.</p>}
            {chosenSubs.map((s, idx) => {
              const hidden = (edition.hidden_section_ids || []).includes(s.id);
              return (
                <div key={s.id} className={`p-3 rounded-lg border border-border flex items-center gap-2 ${hidden ? 'opacity-50' : ''}`} data-testid={`section-${s.id}`}>
                  <span className="text-xs font-bold w-6">{idx + 1}.</span>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{s.department_name} {s.title && <span className="text-muted-foreground">· {s.title}</span>}</p>
                    <p className="text-xs text-muted-foreground line-clamp-1">{s.content}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => moveSection(s.id, -1)} disabled={idx === 0 || isPublished} data-testid={`up-${s.id}`}>↑</Button>
                  <Button size="sm" variant="outline" onClick={() => moveSection(s.id, 1)} disabled={idx === chosenSubs.length - 1 || isPublished} data-testid={`down-${s.id}`}>↓</Button>
                  <Button size="sm" variant="outline" onClick={() => toggleHidden(s.id)} disabled={isPublished} data-testid={`hide-${s.id}`}>{hidden ? 'Show' : 'Hide'}</Button>
                  <Button size="sm" variant="outline" className="text-rose-500" onClick={() => toggleSubmission(s.id)} disabled={isPublished} data-testid={`remove-${s.id}`}>Remove</Button>
                </div>
              );
            })}
          </CardContent></Card>

          <Card><CardContent className="p-6 space-y-3">
            <h3 className="font-semibold flex items-center gap-2"><Plus className="h-4 w-4" />Add Approved Submissions</h3>
            {unchosenSubs.length === 0 && <p className="text-sm text-muted-foreground">No more approved submissions to add. Approve more from Submissions.</p>}
            {unchosenSubs.map(s => (
              <div key={s.id} className="p-3 rounded-lg border border-border flex items-center gap-2" data-testid={`avail-${s.id}`}>
                <div className="flex-1">
                  <p className="font-medium text-sm">{s.department_name} {s.title && <span className="text-muted-foreground">· {s.title}</span>}
                    {s.communicated && <Badge className="ml-2 bg-amber-100 text-amber-700" data-testid={`already-communicated-${s.id}`}>Already Communicated</Badge>}
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-1">{s.content}</p>
                  {s.communicated && <p className="text-xs text-amber-600 mt-0.5">Sent as an immediate communication — including it will duplicate the message.</p>}
                </div>
                <Button size="sm" variant="outline" onClick={() => toggleSubmission(s.id)} disabled={isPublished} data-testid={`add-${s.id}`}>{s.communicated ? 'Include anyway' : 'Add'}</Button>
              </div>
            ))}
          </CardContent></Card>

          {/* Recipients (Excel) */}
          <Card><CardContent className="p-6 space-y-3" data-testid="recipients-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-semibold flex items-center gap-2"><Users className="h-4 w-4" />Recipients</h3>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-recipients-template-btn"><Download className="h-4 w-4 mr-1" />Download Template</Button>
                {!isPublished && (
                  <label className="inline-flex items-center gap-1 cursor-pointer text-sm px-3 py-2 rounded-md border border-border hover:bg-accent" data-testid="upload-recipients-label">
                    {uploadingRec ? <Clock className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload Excel
                    <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => onUploadRecipients(e.target.files?.[0])} data-testid="upload-recipients-input" />
                  </label>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Required to publish. Template columns: Name, Email, Department, Phone. <strong>Phone is mandatory</strong> (numbers default to country code +91; any code typed in is auto-handled). Duplicate emails are removed automatically. This list drives email + WhatsApp distribution, acknowledgement &amp; query tracking, and analytics.</p>
            {recipientSummary ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-3">
                  <div className="px-4 py-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/50" data-testid="recipients-total">
                    <p className="text-xs text-muted-foreground">Total Recipients</p>
                    <p className="text-2xl font-bold text-emerald-600">{recipientSummary.total}</p>
                  </div>
                  {recipientSummary.invalid?.length > 0 && (
                    <div className="px-4 py-3 rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200/50" data-testid="recipients-invalid-count">
                      <p className="text-xs text-muted-foreground">Invalid Records</p>
                      <p className="text-2xl font-bold text-rose-600">{recipientSummary.invalid.length}</p>
                    </div>
                  )}
                </div>
                {recipientSummary.by_department && Object.keys(recipientSummary.by_department).length > 0 && (
                  <div data-testid="recipients-by-dept">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">By Department</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(recipientSummary.by_department).map(([d, c]) => (
                        <Badge key={d} variant="outline" className="text-xs">{d}: {c}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {recipientSummary.invalid?.length > 0 && (
                  <div className="text-xs text-rose-600 space-y-0.5 max-h-32 overflow-y-auto">
                    {recipientSummary.invalid.slice(0, 20).map((r, i) => (
                      <div key={i}>• {r.name || '(no name)'} · {r.email || '(no email)'} — {r.reason}</div>
                    ))}
                  </div>
                )}
                {!isPublished && <Button size="sm" variant="outline" className="text-rose-500" onClick={removeRecipients} data-testid="clear-recipients-btn"><X className="h-3 w-3 mr-1" />Clear list</Button>}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No recipients uploaded yet.</p>
            )}
          </CardContent></Card>

          {/* WhatsApp distribution (Interakt) — available on any saved edition (draft or published) */}
          <Card><CardContent className="p-6 space-y-3" data-testid="whatsapp-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-semibold flex items-center gap-2"><MessageCircle className="h-4 w-4 text-emerald-600" />WhatsApp Distribution</h3>
              <Button size="sm" onClick={doSendWhatsApp} disabled={sendingWa} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-whatsapp-btn">
                {sendingWa ? <Clock className="h-4 w-4 mr-1 animate-spin" /> : <MessageCircle className="h-4 w-4 mr-1" />}Send via WhatsApp
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Sends &quot;📣 The Frequency – {edition.title} is out! Tap to read: …&quot; to every recipient with a phone number via Interakt. Requires an approved WhatsApp template named <code>the_frequency_edition</code>.</p>
            {edition.whatsapp_last_result ? (
              <div className="text-sm space-y-1" data-testid="whatsapp-last-result">
                <div className="flex flex-wrap gap-3">
                  <Badge variant="outline" className="text-emerald-700 border-emerald-300">Sent: {edition.whatsapp_last_result.sent}</Badge>
                  {edition.whatsapp_last_result.failed > 0 && <Badge variant="outline" className="text-rose-600 border-rose-300">Failed: {edition.whatsapp_last_result.failed}</Badge>}
                  <span className="text-xs text-muted-foreground">Last run: {new Date(edition.whatsapp_last_result.at).toLocaleString()}</span>
                </div>
                {edition.whatsapp_last_result.errors?.length > 0 && (
                  <div className="text-xs text-rose-600 space-y-0.5 max-h-32 overflow-y-auto mt-1">
                    {edition.whatsapp_last_result.errors.map((er, i) => <div key={i}>• {er}</div>)}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Not sent yet.</p>
            )}
          </CardContent></Card>
        </>
      )}

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto p-0">
          <DialogHeader className="p-4 border-b border-border"><DialogTitle>Newsletter Preview</DialogTitle></DialogHeader>
          <iframe srcDoc={previewHtml} className="w-full" style={{ height: '70vh', border: '0' }} title="preview" data-testid="preview-iframe" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== SUBMISSIONS (Contributor / Marketing Admin) ==============
function FrequencySubmissionsPage({ canManage }) {
  const { user } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [myDept, setMyDept] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', key_metrics: '', message_to_marketing: '', attachments: [] });
  const [existingSub, setExistingSub] = useState(null);
  const [allSubs, setAllSubs] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSub, setDetailSub] = useState(null);

  const reload = async () => {
    const d = await getFrequencyDepartments();
    setDepartments(d.data);
    const mine = d.data.find(dep => (dep.owner_email || '').toLowerCase() === (user?.email || '').toLowerCase());
    setMyDept(mine || null);
    const subs = await getFrequencySubmissions();
    setAllSubs(subs.data);
    if (mine) {
      // Latest unassigned (no edition) draft/returned/submitted for this dept
      const open = subs.data.find(s => s.department_id === mine.id && !s.edition_id && ['Draft', 'Submitted', 'Returned'].includes(s.status));
      if (open) {
        setExistingSub(open);
        setForm({ title: open.title || '', content: open.content || '', key_metrics: open.key_metrics || '', message_to_marketing: open.message_to_marketing || '', attachments: open.attachments || [] });
      } else { setExistingSub(null); setForm({ title: '', content: '', key_metrics: '', message_to_marketing: '', attachments: [] }); }
    }
  };
  useEffect(() => { reload(); }, [user?.email]);

  const fileToDataUrl = (f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(f);
  });

  const onAddAttachments = async (files) => {
    const allowed = ['application/pdf', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/jpeg', 'image/png'];
    const list = Array.from(files || []);
    const next = [...form.attachments];
    for (const f of list) {
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} exceeds 8MB`); continue; }
      if (!allowed.includes(f.type) && !/\.(pdf|xlsx?|docx?|pptx?|jpe?g|png)$/i.test(f.name)) { toast.error(`${f.name}: unsupported type`); continue; }
      const data_url = await fileToDataUrl(f);
      next.push({ name: f.name, size: f.size, type: f.type, url: data_url });
    }
    setForm({ ...form, attachments: next });
  };
  const removeAttachment = (idx) => setForm({ ...form, attachments: form.attachments.filter((_, i) => i !== idx) });

  const submit = async (status) => {
    if (!myDept) { toast.error('You are not registered as a department owner'); return; }
    try {
      await createFrequencySubmission({ edition_id: null, department_id: myDept.id, ...form, status });
      toast.success(status === 'Draft' ? 'Draft saved' : 'Submitted');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const openDetail = async (sub) => {
    try {
      const r = await getFrequencySubmission(sub.id);
      setDetailSub(r.data);
      setDetailOpen(true);
    } catch (e) { toast.error('Failed to load'); }
  };

  // Group by month (created_at)
  const groupByMonth = (subs) => {
    const map = new Map();
    subs.forEach(s => {
      const d = new Date(s.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
      if (!map.has(key)) map.set(key, { label, items: [] });
      map.get(key).items.push(s);
    });
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0])).map(([_, v]) => v);
  };

  const isLocked = existingSub && !['Draft', 'Returned'].includes(existingSub.status);

  return (
    <div className="space-y-6">
      {!canManage && myDept && (
        <Card><CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-semibold">Create / Update submission for <span className="text-[#F5A623]">{myDept.name}</span></p>
            {existingSub && <Badge className={STATUS_COLOR[existingSub.status]}>{existingSub.status}</Badge>}
          </div>
          {isLocked && <p className="text-xs text-rose-500">Locked — already {existingSub.status}. Marketing must Return to allow edits.</p>}
          {existingSub?.review_notes && existingSub.status === 'Returned' && <div className="p-2 rounded bg-rose-50 dark:bg-rose-950/30 text-sm"><strong>Marketing review notes:</strong> {existingSub.review_notes}</div>}
          <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Short headline for this update" disabled={isLocked} data-testid="sub-title-input" /></div>
          <div><Label>Monthly Update / Highlights *</Label><Textarea rows={6} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} disabled={isLocked} data-testid="sub-content-input" /></div>
          <div><Label>Key Metrics / Numbers</Label><Textarea rows={3} value={form.key_metrics} onChange={e => setForm({ ...form, key_metrics: e.target.value })} disabled={isLocked} /></div>
          <div>
            <Label>Attachments (PDF / DOC / XLSX / PPT / Images)</Label>
            <input type="file" multiple disabled={isLocked} onChange={(e) => onAddAttachments(e.target.files)} className="block mt-1 text-sm" data-testid="sub-attachments-input" />
            <div className="mt-2 space-y-1">
              {form.attachments.map((a, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded border border-border bg-accent/30 text-sm" data-testid={`attach-row-${i}`}>
                  <span className="truncate flex-1">📎 {a.name} <span className="text-xs text-muted-foreground">({Math.round((a.size || 0) / 1024)}KB)</span></span>
                  {!isLocked && <button type="button" onClick={() => removeAttachment(i)} className="text-rose-500 text-xs ml-2">Remove</button>}
                </div>
              ))}
            </div>
          </div>
          <div><Label>Message for Marketing Team</Label><Textarea rows={2} value={form.message_to_marketing} onChange={e => setForm({ ...form, message_to_marketing: e.target.value })} disabled={isLocked} /></div>
          {!isLocked && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => submit('Draft')} data-testid="save-draft-btn">Save Draft</Button>
              <Button onClick={() => submit('Submitted')} disabled={!form.content?.trim()} className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" data-testid="submit-update-btn"><Send className="h-4 w-4 mr-1" />Submit Update</Button>
            </div>
          )}
        </CardContent></Card>
      )}

      {!canManage && !myDept && (
        <Card><CardContent className="p-5 text-sm text-muted-foreground">You are not registered as a department owner in The Frequency. Ask the Marketing Admin to add your department in Settings.</CardContent></Card>
      )}

      <Card><CardContent className="p-5 space-y-4">
        <p className="font-semibold">All Submissions</p>
        {allSubs.length === 0 && <p className="text-sm text-muted-foreground">No submissions yet.</p>}
        {groupByMonth(allSubs).map(grp => (
          <div key={grp.label} className="space-y-2">
            <p className="text-xs font-bold tracking-widest text-muted-foreground border-b border-border pb-1">{grp.label}</p>
            {grp.items.map(s => (
              <button key={s.id} onClick={() => openDetail(s)} className="w-full text-left p-3 rounded-lg border border-border hover:border-[#F5A623]/40 transition-colors" data-testid={`adm-sub-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{s.department_name} {s.title && <span className="text-muted-foreground">· {s.title}</span>}</p>
                    <p className="text-xs text-muted-foreground">by {s.submitted_by_name} · {new Date(s.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge className={STATUS_COLOR[s.status]}>{s.status}</Badge>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{s.content}</p>
              </button>
            ))}
          </div>
        ))}
      </CardContent></Card>

      <SubmissionDetailDialog open={detailOpen} onOpenChange={setDetailOpen} sub={detailSub} canManage={canManage} onAction={reload} />
    </div>
  );
}

function SubmissionDetailDialog({ open, onOpenChange, sub, canManage, onAction }) {
  const [reviewNotes, setReviewNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [immSummary, setImmSummary] = useState(null);
  const [immBusy, setImmBusy] = useState(false);
  useEffect(() => { setReviewNotes(''); setImmSummary(null); }, [sub?.id]);
  if (!sub) return null;
  const doApprove = async () => {
    setBusy(true);
    try { await approveFrequencySubmission(sub.id); toast.success('Approved'); onOpenChange(false); onAction?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const doReturn = async () => {
    if (!reviewNotes.trim()) { toast.error('Please add review notes'); return; }
    setBusy(true);
    try { await returnFrequencySubmission(sub.id, reviewNotes); toast.success('Returned'); onOpenChange(false); onAction?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const parseImmFile = async (file) => {
    if (!file) return;
    try { const r = await parseFrequencyRecipients(file); setImmSummary(r.data); toast.success(`${r.data.total} recipients ready`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Parse failed'); }
  };
  const doKeepForNewsletter = async () => {
    setImmBusy(true);
    try { await keepSubmissionForNewsletter(sub.id); toast.success('Kept for newsletter'); onOpenChange(false); onAction?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setImmBusy(false); }
  };
  const doPublishImmediately = async () => {
    if (!immSummary?.recipients?.length) { toast.error('Upload a recipient list first'); return; }
    setImmBusy(true);
    try { const r = await publishSubmissionImmediately(sub.id, { recipients: immSummary.recipients }); toast.success(`Published immediately — ${r.data.sent_count} recipients`); onOpenChange(false); onAction?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setImmBusy(false); }
  };
  const canPublishImmediate = canManage && sub.status === 'Approved' && !sub.communicated;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{sub.title || sub.department_name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm" data-testid="submission-detail">
          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div><strong>Department:</strong> {sub.department_name}</div>
            <div><strong>Status:</strong> <Badge className={STATUS_COLOR[sub.status]}>{sub.status}</Badge></div>
            <div><strong>Submitted by:</strong> {sub.submitted_by_name}</div>
            <div><strong>Submitted on:</strong> {sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : '—'}</div>
            {sub.communicated && <div><strong>Communicated:</strong> <Badge className="bg-amber-100 text-amber-700">{sub.communicated_via === 'immediate' ? 'Immediately' : 'In newsletter'}</Badge></div>}
          </div>
          <div>
            <p className="font-medium mb-1">Content</p>
            <p className="whitespace-pre-wrap">{sub.content || '—'}</p>
          </div>
          {sub.key_metrics && <div><p className="font-medium mb-1">Key Metrics</p><p className="whitespace-pre-wrap">{sub.key_metrics}</p></div>}
          {sub.attachments?.length > 0 && (
            <div>
              <p className="font-medium mb-1">Attachments</p>
              <ul className="space-y-1 text-sm">{sub.attachments.map((a, idx) => (<li key={idx}><a href={a.url} download={a.name} className="text-blue-600 hover:underline">📎 {a.name}</a></li>))}</ul>
            </div>
          )}
          {sub.message_to_marketing && <div className="p-2 rounded bg-accent text-sm"><strong>Message for Marketing:</strong> {sub.message_to_marketing}</div>}
          {sub.review_notes && sub.status === 'Returned' && <div className="p-2 rounded bg-rose-50 dark:bg-rose-950/30 text-sm"><strong>Marketing Comments:</strong> {sub.review_notes}</div>}
        </div>
        {canManage && sub.status === 'Submitted' && (
          <div className="space-y-2 pt-3 border-t border-border">
            <Textarea rows={2} placeholder="Review notes (required when returning)…" value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} data-testid="review-notes-input" />
            <DialogFooter>
              <Button variant="outline" onClick={doReturn} disabled={busy} data-testid="return-sub-btn"><RotateCcw className="h-3 w-3 mr-1" />Return</Button>
              <Button onClick={doApprove} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-sub-btn"><CheckCircle2 className="h-3 w-3 mr-1" />Approve</Button>
            </DialogFooter>
          </div>
        )}
        {canPublishImmediate && (
          <div className="space-y-3 pt-3 border-t border-border" data-testid="immediate-actions">
            <p className="text-sm font-semibold flex items-center gap-1"><Zap className="h-4 w-4 text-amber-500" />How do you want to communicate this?</p>
            <p className="text-xs text-muted-foreground">Marketing decides: publish immediately to recipients now, or keep it for the next monthly newsletter.</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={async () => { try { const r = await downloadFrequencyRecipientsTemplate(); const url = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = 'frequency_recipients_template.xlsx'; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url); } catch { toast.error('Download failed'); } }} data-testid="imm-template-btn"><Download className="h-4 w-4 mr-1" />Template</Button>
              <label className="inline-flex items-center gap-1 cursor-pointer text-sm px-3 py-2 rounded-md border border-border hover:bg-accent" data-testid="imm-upload-label">
                <Upload className="h-4 w-4" /> Upload Recipients
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => parseImmFile(e.target.files?.[0])} data-testid="imm-upload-input" />
              </label>
              {immSummary && <Badge className="bg-emerald-100 text-emerald-700" data-testid="imm-recipient-count">{immSummary.total} recipients{immSummary.invalid?.length ? ` · ${immSummary.invalid.length} invalid` : ''}</Badge>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={doKeepForNewsletter} disabled={immBusy} data-testid="keep-for-newsletter-btn">Keep for Newsletter</Button>
              <Button onClick={doPublishImmediately} disabled={immBusy || !immSummary?.total} className="bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="publish-immediately-btn"><Zap className="h-3 w-3 mr-1" />Publish Immediately</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============== QUERIES ==============
function FrequencyQueriesPage({ canManage }) {
  const [queries, setQueries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [responseDraft, setResponseDraft] = useState({});
  const location = useLocation();
  const highlightId = new URLSearchParams(location.search).get('q');

  const load = async () => {
    try {
      const [q, d] = await Promise.all([getFrequencyQueries(), getFrequencyDepartments(true)]);
      setQueries(q.data); setDepartments(d.data);
    } catch (_) { /* noop */ }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (highlightId && queries.length) {
      const el = document.querySelector(`[data-testid="query-card-${highlightId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightId, queries]);

  const assign = async (qid, deptId) => {
    try { await assignFrequencyQuery(qid, deptId); toast.success('Assigned'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const respond = async (qid) => {
    const text = responseDraft[qid] || '';
    if (!text.trim()) return;
    try { await respondFrequencyQuery(qid, text); setResponseDraft({ ...responseDraft, [qid]: '' }); toast.success('Response sent'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const setStatus = async (qid, s) => {
    try { await updateFrequencyQueryStatus(qid, s); toast.success('Updated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3">
      {queries.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No queries yet.</p>}
      {queries.map(q => (
        <Card key={q.id} data-testid={`query-card-${q.id}`} className={highlightId === q.id ? 'ring-2 ring-[#7C3AED] ring-offset-2' : ''}>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-semibold">{q.subject}</p>
                <p className="text-xs text-muted-foreground">From {q.employee_name || q.employee_email} {q.department && `· ${q.department}`} · {new Date(q.created_at).toLocaleString()}</p>
              </div>
              <div className="flex gap-2 items-center">
                <Badge className={STATUS_COLOR[q.status]}>{q.status}</Badge>
                {q.assigned_dept_name && <Badge>{q.assigned_dept_name}</Badge>}
              </div>
            </div>
            <p className="text-sm whitespace-pre-wrap">{q.description}</p>
            {q.responses?.length > 0 && (
              <div className="space-y-1 pl-3 border-l-2 border-[#F5A623]/50 text-sm">
                {q.responses.map((r, i) => (
                  <div key={i}><strong>{r.by}:</strong> {r.text} <span className="text-xs text-muted-foreground">({new Date(r.at).toLocaleString()})</span></div>
                ))}
              </div>
            )}
            <div className="flex gap-2 flex-wrap items-center">
              {canManage && (
                <Select value={q.assigned_dept_id || ''} onValueChange={(v) => assign(q.id, v)}>
                  <SelectTrigger className="w-48 h-8 text-xs"><SelectValue placeholder="Assign to dept" /></SelectTrigger>
                  <SelectContent>{departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Select value={q.status} onValueChange={(v) => setStatus(q.id, v)}>
                <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{['New', 'Assigned', 'In Progress', 'Resolved', 'Closed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 items-center">
              <Input value={responseDraft[q.id] || ''} onChange={e => setResponseDraft({ ...responseDraft, [q.id]: e.target.value })} placeholder="Reply…" className="text-sm h-8" data-testid={`query-response-${q.id}`} />
              <Button size="sm" onClick={() => respond(q.id)} className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold"><Send className="h-3 w-3" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ============== ANALYTICS ==============
function FrequencyAnalyticsPage() {
  const [editions, setEditions] = useState([]);
  const [selected, setSelected] = useState('');
  const [data, setData] = useState(null);
  const [immediate, setImmediate] = useState([]);
  useEffect(() => { (async () => {
    const e = await getFrequencyEditions();
    setEditions(e.data);
    const pub = e.data.find(ed => ed.status === 'Published') || e.data[0];
    if (pub) setSelected(pub.id);
    try { const im = await getFrequencyImmediateAnalytics(); setImmediate(im.data.items || []); } catch { /* noop */ }
  })(); }, []);
  useEffect(() => { (async () => {
    if (!selected) return;
    const r = await getFrequencyEditionAnalytics(selected);
    setData(r.data);
  })(); }, [selected]);
  return (
    <div className="space-y-4">
      <Select value={selected} onValueChange={setSelected}>
        <SelectTrigger className="w-80" data-testid="analytics-edition-select"><SelectValue placeholder="Select edition" /></SelectTrigger>
        <SelectContent>{editions.map(e => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}</SelectContent>
      </Select>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <StatCard label="Sent" value={data.sent} accent="from-[#4D9BF5] to-[#7B7DFF]" testid="anlt-sent" />
            <StatCard label="WhatsApp Sent" value={data.whatsapp_sent || 0} accent="from-[#25D366] to-[#128C7E]" testid="anlt-wa-sent" />
            <StatCard label="Delivered" value={data.delivered} accent="from-[#6BCB77] to-[#3DDC97]" testid="anlt-delivered" />
            <StatCard label="Opened" value={data.opened} accent="from-[#FFD93D] to-[#FFA94D]" testid="anlt-opened" />
            <StatCard label="Clicked" value={data.clicked} accent="from-[#FF8FB1] to-[#FF6B6B]" testid="anlt-clicked" />
            <StatCard label="Acknowledged" value={data.acknowledged} accent="from-[#6BCB77] to-[#3DDC97]" testid="anlt-ack" />
            <StatCard label="Pending Ack" value={data.pending_ack} accent="from-[#FF6B6B] to-[#FFA94D]" testid="anlt-pending-ack" />
            <StatCard label="Queries Raised" value={data.queries_raised || 0} accent="from-[#7B7DFF] to-[#9D6BFF]" testid="anlt-queries" />
          </div>
          <Card><CardContent className="p-5">
            <h3 className="font-semibold mb-3">By Department</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-muted-foreground border-b border-border"><th className="text-left py-2">Department</th><th>Sent</th><th>Opened</th><th>Acknowledged</th><th>Read Rate</th><th>Ack Rate</th></tr></thead>
                <tbody>{data.by_department.map(d => <tr key={d.department} className="border-b border-border/30"><td className="py-2">{d.department || '—'}</td><td className="text-center">{d.sent}</td><td className="text-center">{d.opened}</td><td className="text-center">{d.acknowledged}</td><td className="text-center">{d.read_rate}%</td><td className="text-center">{d.ack_rate}%</td></tr>)}</tbody>
              </table>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-5">
            <h3 className="font-semibold mb-3">Recipients</h3>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-muted-foreground border-b border-border"><th className="text-left py-2">Email</th><th className="text-left">Department</th><th className="text-left">Channel</th><th>Sent</th><th>Opened</th><th>Ack</th></tr></thead>
                <tbody>{data.recipients.map((r, i) => (<tr key={i} className="border-b border-border/30"><td className="py-1">{r.recipient_email}</td><td>{r.recipient_department}</td><td className="text-xs">{r.channel || '—'}</td><td className="text-center text-xs">{(r.sent_at || r.whatsapp_sent_at) ? '✓' : ''}</td><td className="text-center text-xs">{r.opened_at ? '✓' : ''}</td><td className="text-center text-xs">{r.acknowledged_at ? '✓' : ''}</td></tr>))}</tbody>
              </table>
            </div>
          </CardContent></Card>
        </>
      )}
      <Card data-testid="immediate-analytics"><CardContent className="p-5">
        <h3 className="font-semibold mb-1 flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" />Immediate Communications</h3>
        <p className="text-xs text-muted-foreground mb-3">Tracked separately from newsletter editions.</p>
        {immediate.length === 0 ? (
          <p className="text-sm text-muted-foreground">No immediate communications published yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs text-muted-foreground border-b border-border"><th className="text-left py-2">Title</th><th className="text-left">Published</th><th>Sent</th><th>Opened</th><th>Acknowledged</th><th>Queries</th></tr></thead>
              <tbody>{immediate.map(it => (<tr key={it.edition_id} className="border-b border-border/30"><td className="py-2">{it.title}</td><td className="text-xs">{it.published_at ? new Date(it.published_at).toLocaleString() : '—'}</td><td className="text-center">{it.sent}</td><td className="text-center">{it.opened}</td><td className="text-center">{it.acknowledged}</td><td className="text-center">{it.queries_raised}</td></tr>))}</tbody>
            </table>
          </div>
        )}
      </CardContent></Card>
    </div>
  );
}
